import React, {
	forwardRef,
	useState,
} from 'react';
import {
	Button as AntButton,
	ConfigProvider,
	Popconfirm,
	Tooltip,
} from 'antd';
import { ButtonProps as AntButtonProps } from 'antd/lib/button';
import { PopconfirmProps } from 'antd/lib/popconfirm';
import { TooltipProps } from 'antd/lib/tooltip';
import classNames from 'classnames';
import { isPromise } from '@shared/utils/promise';
import { ExtendUnique } from '@client/utils/ExtendUnique';
import styles from './styles/Button.module.css';

export type ButtonProps = ExtendUnique<{
	disabled?: boolean;
	popconfirmProps?: Omit<PopconfirmProps, 'title'>;
	disabledTooltip?: string | React.ReactNode;
	confirmTitle?: string | React.ReactNode;
	tooltipProps?: TooltipProps;
	padding?: number;
	clickPropagation?: boolean;
	onClick?: (e: React.MouseEvent<HTMLElement> | undefined) => void;
	pulse?: boolean;
}, AntButtonProps>;

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(({
	disabled,
	popconfirmProps = { style: { padding: 8 } },
	disabledTooltip,
	confirmTitle,
	tooltipProps,
	padding,
	children,
	onClick,
	type,
	clickPropagation = true,
	className,
	pulse = false,
	...props
}, ref) => {
	const hasPopconfirm = (confirmTitle != null);
	const [loading, setLoading] = useState(false);
	const { componentDisabled } = ConfigProvider.useConfig();

	const click = async (e: React.MouseEvent<HTMLElement> | undefined, isConfirming?: boolean) => {
		if (!clickPropagation) {
			e?.stopPropagation();
		}

		if (hasPopconfirm && !isConfirming) {
			return;
		}

		if (typeof onClick === 'function') {
			const returnValue = onClick(e);

			// If returnValue is a promise, handle loading state
			if (isPromise(returnValue)) {
				setLoading(true);

				// makes sure to always setLoading false, if there is an error
				try {
					await returnValue;
				} finally {
					setLoading(false);
				}
			}
		}
	};

	const content = (
		<AntButton
			loading={loading}
			ref={ref}
			onClick={click}
			disabled={disabled || componentDisabled}
			className={classNames(className, {
				[styles.pulseBtn]: pulse,
			})}
			type={type}
			style={{ padding }}
			{...props}
		>
			{children}
		</AntButton>
	);

	if (disabled && disabledTooltip != null) {
		return (
			<Tooltip
				title={disabledTooltip}
				style={{ whiteSpace: 'pre-line' }}
				{...tooltipProps}
			>
				{content}
			</Tooltip>
		);
	}

	if (hasPopconfirm) {
		return (
			<Popconfirm
				trigger={disabled ? [] : 'click'}
				arrow={{ pointAtCenter: true }}
				placement="bottomLeft"
				{...popconfirmProps}
				title={confirmTitle}
				onConfirm={(e) => click(e, true)}
			>
				{content}
			</Popconfirm>
		);
	}

	return content;
});

export default Button;
